/**
 * 3列コンパクト表: ドラッグ範囲選択 + Ctrl+C コピー（t検定2列表と同趣旨）
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import grid from './KeisukeUnifiedDataGrid.module.scss';

export type ThreeColGridCell = { row: number; col: 0 | 1 | 2 };

export type ThreeColGridSelection = { a: ThreeColGridCell; b: ThreeColGridCell };

function threeColInputFromEventTarget(target: EventTarget | null): HTMLInputElement | null {
  if (!(target instanceof HTMLElement)) return null;
  const inp = target.closest('input');
  if (!inp || inp.tagName !== 'INPUT') return null;
  return inp as HTMLInputElement;
}

function threeColCellFromEventTarget(target: EventTarget | null): ThreeColGridCell | null {
  const inp = threeColInputFromEventTarget(target);
  if (!inp?.id) return null;
  const m = inp.id.match(/-r(\d+)-c([012])$/);
  if (!m) return null;
  return { row: Number(m[1]), col: Number(m[2]) as 0 | 1 | 2 };
}

function normalizeThreeColSelection(sel: ThreeColGridSelection): ThreeColGridSelection {
  const rMin = Math.min(sel.a.row, sel.b.row);
  const rMax = Math.max(sel.a.row, sel.b.row);
  const cMin = Math.min(sel.a.col, sel.b.col) as 0 | 1 | 2;
  const cMax = Math.max(sel.a.col, sel.b.col) as 0 | 1 | 2;
  return { a: { row: rMin, col: cMin }, b: { row: rMax, col: cMax } };
}

function threeColCellInSelection(row: number, col: 0 | 1 | 2, sel: ThreeColGridSelection | null): boolean {
  if (!sel) return false;
  const n = normalizeThreeColSelection(sel);
  return row >= n.a.row && row <= n.b.row && col >= n.a.col && col <= n.b.col;
}

function buildTsvFromThreeColRows(
  headers: [string, string, string],
  rows: [string, string, string][],
  sel: ThreeColGridSelection
): string {
  const n = normalizeThreeColSelection(sel);
  const lines: string[] = [];
  if (n.a.row === -1 || (n.a.row === 0 && n.b.row === 0 && n.a.col <= 2)) {
    /* header row selection not used; data only */
  }
  for (let r = n.a.row; r <= n.b.row; r++) {
    const row = rows[r] ?? ['', '', ''];
    const cells: string[] = [];
    for (let c = n.a.col; c <= n.b.col; c++) {
      cells.push(row[c] ?? '');
    }
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
}

export function useKeisukeThreeColumnGridClipboard(
  valueRows: [string, string, string][],
  disabled: boolean
) {
  const tableRef = useRef<HTMLTableElement>(null);
  const gridAnchorRef = useRef<ThreeColGridCell | null>(null);
  const [gridSelection, setGridSelection] = useState<ThreeColGridSelection | null>(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);

  useEffect(() => {
    if (!isDragSelecting) return;
    const onMove = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = threeColCellFromEventTarget(el);
      if (cell && gridAnchorRef.current) {
        setGridSelection({ a: gridAnchorRef.current, b: cell });
      }
    };
    const onUp = () => setIsDragSelecting(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [isDragSelecting]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current?.contains(t)) return;
      setGridSelection(null);
      gridAnchorRef.current = null;
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, []);

  const onTablePointerDownCapture = useCallback(
    (e: React.PointerEvent<HTMLTableElement>) => {
      if (disabled || e.button !== 0) return;
      const cell = threeColCellFromEventTarget(e.target);
      if (!cell) return;
      if (e.shiftKey && gridAnchorRef.current) {
        setGridSelection({ a: gridAnchorRef.current, b: cell });
        return;
      }
      gridAnchorRef.current = cell;
      setGridSelection({ a: cell, b: cell });
      setIsDragSelecting(true);
    },
    [disabled]
  );

  const onTableCopyCapture = useCallback(
    (e: React.ClipboardEvent<HTMLTableElement>) => {
      if (disabled) return;
      const inp = threeColInputFromEventTarget(e.target);
      if (!inp) return;
      const focusedCell = threeColCellFromEventTarget(inp);
      const effectiveSel =
        gridSelection ??
        (focusedCell ? ({ a: focusedCell, b: focusedCell } satisfies ThreeColGridSelection) : null);
      if (!effectiveSel) return;
      const norm = normalizeThreeColSelection(effectiveSel);
      const area = (norm.b.row - norm.a.row + 1) * (norm.b.col - norm.a.col + 1);
      if (area <= 1) {
        if (
          inp.selectionStart != null &&
          inp.selectionEnd != null &&
          inp.selectionStart !== inp.selectionEnd
        ) {
          return;
        }
      }
      e.preventDefault();
      e.stopPropagation();
      const tsv = buildTsvFromThreeColRows(['', '', ''], valueRows, norm);
      e.clipboardData?.setData('text/plain', tsv);
    },
    [disabled, gridSelection, valueRows]
  );

  const onTableKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      if (disabled) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setGridSelection(null);
        gridAnchorRef.current = null;
      }
    },
    [disabled]
  );

  const gridCellClass = useCallback(
    (row: number, col: 0 | 1 | 2) =>
      threeColCellInSelection(row, col, gridSelection) ? grid.tTestTwoColCellSelected : undefined,
    [gridSelection]
  );

  return {
    tableRef,
    onTablePointerDownCapture,
    onTableCopyCapture,
    onTableKeyDownCapture,
    gridCellClass,
  };
}
